import search from '@/components/search/searchStore';
import sendbox from '@/components/sendbox/sendboxStore';
import snackbar from '@/components/snackbar/snackbarStore';
import { stores as moduleStores } from '@/modules/index';
import configs from '@/store/configs/configs';
import history from '@/store/history/history';
import language from '@/store/language/language';
import profile from '@/store/profile/store';
import sync from '@/store/sync/sync';
import theme from '@/store/theme/theme';
import tabview from '@/views/tabview/store';
import welcome from '@/screens/welcome/welcomeStore';
import Vue from 'vue';
import Vuex from 'vuex';
import lifecycle from './lifecycle/lifecycleStore';
import remoteLogger from '@/api/remoteLoggerApi';
import usersApi from '@/api/usersApi';

const DRAWER_BREAKPOINT = 1100;
Vue.use(Vuex);
let serverAvailabilityCheckInterval;
let userAccessCheckInterval;

export const state = {
  // common application states
  isPwaMode: process.env.VUE_APP_PWA_ENABLED === 'true',
  isNavigationDrawerOpen: false,
  orientation: 'portrait', //window.screen.orientation.type === 'portrait-primary' ? 'portrait' : 'landscape',
  isConnected: true, // indicates the network connection
  connectionType: null, // 2g, 3g, 4g, 5g
  isServerReachable: null, // true to indicate the server can be reached (connection on plus server is running)
  isToolbarsHidden: false,
  acknowledgeDialog: false, // open/close state of acknowledge dialog (quittierung)
  acknowledgeDocument: null, // {storeModule,document} vuex store module and Document to acknowledge
  acknowledgeNavigationNext: null, // router next function to delay navigation until acknowledgement has been completed
  leftDrawer: false, // leftDrawer open state
  mx_needsDrawer: null, //true if the viewport is < 1100px and needs a drawer (non-persistent)
  screenAngle: null,
  contentHeight: null, //THe content area height (calculated when windows size and/or orientation changes happen)
};

export const mutations = {
  onWindowsResize: (state) => {
    let width = window.innerWidth; //screen.width; //app.offsetWidth;
    let height = window.innerHeight; //screen.height; // app.offsetHeight;
    if (screen.orientation) {
      // supported on chromium
      // console.log('notification "onWindowsResize" received with: width=', width, 'height=', height, 'screen.orientation', screen.orientation.type);
      let mx_needsDrawer = width < DRAWER_BREAKPOINT;
      let screenAngle = screen.orientation.angle;
      let contentHeight = height - (state.isToolbarsHidden ? 0 : 110);

      if (state.mx_needsDrawer !== mx_needsDrawer || state.screenAngle !== screenAngle || state.contentHeight !== contentHeight) {
        state.mx_needsDrawer = mx_needsDrawer;
        state.screenAngle = screenAngle;
        state.contentHeight = contentHeight;
        console.debug(
          `onWindowsResize resulted with changing mx_needsDrawer to ${state.mx_needsDrawer} contentHeight:${state.contentHeight} or screenAngle ${screenAngle}`
        );
      }
    } else {
      // safari support
      let mql = window.matchMedia('(orientation: portrait)');
      let angle;
      // If there are matches, we're in portrait
      if (mql.matches) {
        // Portrait orientation
        angle = 0;
      } else {
        // Landscape orientation
        angle = 90;
      }
      console.debug(`"onWindowsResize" event received with: width=${width} height=${height} screen.orientation ${mql.matches ? 'Portrait' : 'Landscape'}`);

      let mx_needsDrawer = width < DRAWER_BREAKPOINT;
      let screenAngle = angle;
      let contentHeight = height - (state.isToolbarsHidden ? 0 : 110);

      if (state.mx_needsDrawer !== mx_needsDrawer || state.screenAngle !== screenAngle || state.contentHeight !== contentHeight) {
        state.mx_needsDrawer = mx_needsDrawer;
        state.screenAngle = screenAngle;
        state.contentHeight = contentHeight;
        console.log(
          `onWindowsResize resulted with changing mx_needsDrawer to ${state.mx_needsDrawer} contentHeight:${state.contentHeight} or screenAngle ${screenAngle}`
        );
      }
    }
  },
  setState: (state, { property, value }) => (state[property] = value),
  set: (rootState, { module, property, value }) => {
    console.debug(`set store at state.${module} ${property} ${value} `);
    rootState[module][property] = value;
  },
  setIsConnected(state, isConnected) {
    state.isConnected = isConnected;
    if (!isConnected) state.connectionType = '';
  },
  setConnectionType(state, connectionType) {
    state.connectionType = connectionType;
  },
  setIsServerReachable(state, isServerReachable) {
    state.isServerReachable = isServerReachable;
  },
  displaySnackbar: (state, { text, color = 'info', durationInSeconds = 2000 }) => {
    state.snackbar = { isOpen: true, color, text, durationInSeconds };
  },
  closeSnackbar: (state) => (state.snackbar = { isOpen: false }),
  setIsToolbarsHidden: (state, isToolbarsHidden) => (state.isToolbarsHidden = isToolbarsHidden),
  hideNavigationDrawer: (state) => (state.isNavigationDrawerOpen = false),
  toggleNavigationDrawer: (state) => (state.isNavigationDrawerOpen = !state.isNavigationDrawerOpen),

  setAcknowledgeDialog: (state, { value, acknowledgeCompleted }) => {
    console.debug('setAcknowledgeDialog', value, acknowledgeCompleted);
    state.acknowledgeDialog = value;
    if (acknowledgeCompleted && state.acknowledgeNavigationNext) {
      state.acknowledgeNavigationNext();
    }
  },
  setAcknowledgeDocument: (state, { storeModule, document, next }) => {
    state.acknowledgeDocument = { storeModule, document };
    state.acknowledgeDialog = true;
    state.acknowledgeNavigationNext = next;
  },
  setLeftDrawer: (state, value) => {
    state.leftDrawer = value;
  },
};
export default new Vuex.Store({
  strict: false, //process.env.NODE_ENV !== 'production',
  state,
  getters: {},

  mutations,

  actions: {
    /**
     * Set the isServerReachable state to true when it changes from false to true. Initiate a sync in this case.
     * Ignore the request if the state is already true.
     * Otherwise falsify the isServerReachable without any other action.
     * @param {*} param0
     * @param {*} isServerReachable
     */
    setIsServerReachable({ state, commit, dispatch }, isServerReachable) {
      if (isServerReachable) {
        if (!state.isServerReachable) {
          // set state to true but only if the state changes from false to true
          commit('setIsServerReachable', true);
          dispatch('sync/synchronizeAppIfNotSynching');
        }
      } else {
        // report change only
        if (state.isServerReachable) commit('setIsServerReachable', false);
      }
    },
    startServerReachabilityCheck() {
      if (serverAvailabilityCheckInterval) {
        clearInterval(serverAvailabilityCheckInterval);
      }
      serverAvailabilityCheckInterval = setInterval(() => {
        remoteLogger.unauthorizedLog('ServerAvailCheck', 'Check if server is available');
      }, 10 * 1000);
      console.log('startServerReachabilityCheck');
    },
    stopServerReachabilityCheck() {
      if (serverAvailabilityCheckInterval) {
        clearInterval(serverAvailabilityCheckInterval);
      }
      console.log('stopServerReachabilityCheck');
    },
    startUserAccessCheck() {
      if (userAccessCheckInterval) {
        clearInterval(userAccessCheckInterval);
      }
      userAccessCheckInterval = setInterval(() => {
        usersApi.checkAccess();
      }, 60 * 1000);
      console.log('startUserAccessCheck');
    },
    stopUserAccessCheck() {
      if (userAccessCheckInterval) {
        clearInterval(userAccessCheckInterval);
      }
      console.log('stopUserAccessCheck');
    },
    /**
     * Delegates the synchronization of the app to sync/forceSynchronizeApp
     * @param {*} param0
     */
    forceSynchronizeApp({ state, dispatch }) {
      dispatch('sync/forceSynchronizeApp');
    },
    /**
     * Delegates the synchronization of the app to sync/synchronizeApp.
     * All modules which are not in sync state COMPLETED are synchronized, then the sendbox is sent.
     * @param {*} cacheFiles true to indicate files need caching (usually in pwa mode)
     */
    synchronizeApp({ state, dispatch }) {
      console.log('synchronizeApp');
      dispatch('sync/synchronizeApp', { cacheFiles: state.isPwaMode });
    },
  },
  modules: {
    lifecycle,
    sync,
    configs,
    sendbox,
    history,
    theme,
    search,
    profile,
    language,
    snackbar,
    welcome,

    // modules
    ...moduleStores,

    //views
    tabview,
  },
});
